// MACHINERY V2 (Stroje) — V3 table: data-side cells (M2.13).
//
// AKTUÁLNE · 30 DNÍ · OTV. SR. The identity-side cells live in
// fleetTableCells.tsx — split for the 300-line cap.
//
// >>> DATA HONESTY IS THE POINT OF THIS FILE. <<< Every branch renders only what
// the wire actually carries and shows "—" where there is nothing. The gateway
// summary endpoint sends the decoded status and NOTHING else — no program, no
// alarm code/message, no rpm, no part count, no since/heartbeat
// (fleetTelemetry.ts:14-21, personally re-read while writing this file) — so on
// today's data most rows resolve to a dash. The richer branches light up
// unchanged when the deferred §6 telemetry lands. Nothing here fabricates.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { Machine } from "../../../models/models";
import { MachineryV2CardPresentation, MachineryV2Telemetry } from "../cardStates";
import { statusColors } from "../cardStateStyles";
import { healthColor } from "../fleetCardAtoms";
import { Dash } from "./fleetTableCells";

export interface DataCellProps {
  presentation: MachineryV2CardPresentation;
  telemetry: MachineryV2Telemetry;
}

/** 240px is the handoff's own cap on this column's inner text
 *  (machinery-v3-table.jsx: `maxWidth: 240` on both AKTUÁLNE lines). */
const CURRENT_MAX_WIDTH = 240;

const Secondary: React.FC<{ text: string; mono?: boolean }> = ({ text, mono }) => (
  <div
    style={{
      maxWidth: CURRENT_MAX_WIDTH,
      fontSize: mono ? 10.5 : 11.5,
      fontFamily: mono ? "var(--font-mono)" : "inherit",
      color: mono ? "var(--faint)" : "var(--muted)",
      marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    }}
  >
    {text}
  </div>
);

/**
 * AKTUÁLNE (250) — what this machine is doing right now.
 *
 * The branch ORDER mirrors FleetRowJobCell (alarm > program > no-telemetry >
 * everything else) so a table row and a grouped row can never describe the same
 * machine differently. It reads presentation FLAGS only — never `alarms.length`,
 * `status === "running"` or a raw `conn`.
 *
 * THE FINAL BRANCH DIFFERS FROM THE ROW'S, DELIBERATELY, AND THIS IS NOT A
 * DISAGREEMENT: the grouped row has no separate status column, so it labels the
 * cell with the status; the table carries that same label in STAV two columns
 * to the left, so repeating it here would be noise. What is left that STAV does
 * NOT already say is the freshness stamp — and a dash when even that is absent.
 * The artboard fills this branch with free-text job descriptions ("Setup —
 * výmena upínača"); we carry no job field on the wire and do not invent one.
 */
export const CurrentCell: React.FC<DataCellProps> = ({ presentation: p, telemetry: t }) => {
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");

  if (p.showAlarmStrip && t.alarms.length > 0) {
    const alarm = t.alarms[0];
    return (
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            maxWidth: CURRENT_MAX_WIDTH,
            fontSize: 12.5, fontWeight: 600, fontFamily: "var(--font-mono)",
            color: "color-mix(in oklab, var(--red) 80%, var(--ink))",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}
        >
          {p.alarmLastKnown
            ? translate("myMachinery:v2.fleet.alarmLastKnown") + " · " + alarm.code
            : alarm.code}
        </div>
        {alarm.message && <Secondary text={alarm.message} />}
      </div>
    );
  }

  if (p.showProgramLine && t.program) {
    const lang = getCurrentLanguage();
    const parts = [
      t.partCount != null
        ? t.partCount.toLocaleString(lang) + " " + translate("myMachinery:v2.pieces")
        : null,
      t.spindleRpm != null ? t.spindleRpm.toLocaleString(lang) + " RPM" : null,
    ].filter(Boolean) as string[];
    return (
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            maxWidth: CURRENT_MAX_WIDTH,
            fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--ink-2)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}
        >
          <span style={{ color: "var(--green)" }}>{"▶ "}</span>
          {t.program}
        </div>
        {parts.length > 0 && <Secondary text={parts.join(" · ")} mono />}
      </div>
    );
  }

  const freshness = t.heartbeat || t.since || null;
  if (p.showNoTelemetry || !freshness) return <Dash size={12} />;

  return (
    <div
      style={{
        maxWidth: CURRENT_MAX_WIDTH,
        fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--faint)",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}
    >
      {freshness}
    </div>
  );
};

/**
 * 30 DNÍ (120) — DATA-GATED. Ships as an empty state, on purpose.
 *
 * The artboard draws 30 bars whose HEIGHTS encode per-day activity magnitude.
 * Nothing we can read carries that: the fleet DTO has no per-day series and the
 * gateway batch endpoint returns one current status per machine
 * (fleetTelemetry.ts:14-21). Deriving issue-days from service-report history
 * would mean an N+1 per-machine fetch on every fleet load — rejected in the
 * M2.13 ticket row's own assessment.
 *
 * >>> DEPENDENCY: §6 Layer 2 (Connectivity Backend) — "30-day per-machine daily
 * rollup in the batch DTO", which the ticket row instructs be ADDED to the
 * Layer-2 ask when §6 is filed. Until that ships, this cell renders a dash and
 * says why on hover. Do NOT fill it with synthesised bars: the handoff
 * prototype's V3Sparkline generates its series from `Math.sin(id.charCodeAt(1))`
 * — a decorative placeholder, not data. <<<
 */
export const SparklineCell: React.FC = () => {
  const { translate } = useTranslation("myMachinery");
  return (
    <span
      title={translate("myMachinery:v2.fleet.table.sparklinePending")}
      style={{ display: "inline-flex", alignItems: "center", height: 18 }}
    >
      <Dash />
    </span>
  );
};

export interface SrCellProps {
  machine: Machine;
  presentation: MachineryV2CardPresentation;
}

/**
 * OTV. SR (80, right) — the open-service-report pill.
 *
 * TWO RULES ARE RECONCILED HERE, and both survive:
 *   · The M2.13 ticket row: "alarm rows get a solid status-red fill + white
 *     text, others a tinted border." Kept verbatim — an alarm row's SR count is
 *     an urgency signal and the artboard shouts it.
 *   · The M2.3 ruling in FleetRowSrChip: SR pressure follows the 0 / 1-2 / 3+
 *     health ramp, "deliberately NOT the artboard's status tint, which colours
 *     SR pressure by whether the spindle happens to be turning". Kept for every
 *     NON-alarm row, so a running machine with 4 open reports is not painted
 *     calm-green by its spindle.
 * The two only ever collided on the alarm row, which the newer ticket row
 * settles. One ramp everywhere else, one shout where the ticket asks for it.
 */
export const SrCell: React.FC<SrCellProps> = ({ machine, presentation: p }) => {
  const { translate } = useTranslation("myMachinery");
  const open = machine.openServiceReportsCount;
  if (!open) return <Dash />;

  const isAlarm = p.state === "alarm" || p.state === "stale-alarm";
  const alarmFill = statusColors("alarm", p.staleLook).fg;
  const ramp = healthColor(open);

  return (
    <span
      title={translate("myMachinery:healthOpenReports", { count: open })}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        minWidth: 26, padding: "2px 8px", borderRadius: 999,
        background: isAlarm ? alarmFill : "var(--card-2)",
        color: isAlarm ? "#fff" : ramp,
        border: "1px solid " + (isAlarm ? alarmFill : ramp),
        fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {open}
    </span>
  );
};
